<script setup>
import { reactive, ref, onMounted, computed, watch } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useEventsStore } from '@/stores/events';

const portal = ref(window.portal);
const labels = ref(window.portal.labels);
const key = ref(window.portal.event_key);

const eventsStore = useEventsStore();
const authStore = useAuthStore();

// Preload featured event from #annual-conference-cta div
const container = document.getElementById('single-event');
const rawData = container?.dataset?.event ?? 'null';

let initialEvent = null;

try {
	initialEvent = JSON.parse(rawData);
} catch (error) {
	console.error('Failed to parse preloaded events JSON:', error);
}

const fetched = computed(() => {
	return eventsStore.eventFetched(key.value);
});

const event = computed(() => {
	if (fetched.value) {
		return eventsStore.getEvent(key.value);
	}

	return initialEvent ? initialEvent : null;
});

const fetchEvent = async() => {
	try {
		const response = await axios.get(`${window.portal.events_url}/${key.value}`);

		eventsStore.setEvent(response.data, key.value);
	}
	catch (error) {

	} finally {

	}
}

//Fetch every time and update store
fetchEvent();

const activeTab = ref(event.value ? event.value.tabs[0].key : null);

watch(() => event.value, (newVal, oldVal) => {
    if (newVal && activeTab.value == null) {
    	activeTab.value = event.value.tabs[0].key;
    }
});

const delegateLabel = computed(() => {
	return authStore.isLoggedIn ? labels.value.member : labels.value.delegate;
});

const mainActivity = computed(() => {
	if (event.value) {
		const tab = event.value.tabs.find(tab => tab.key == 'activities');

		if (tab) {
			return tab.activities[0];
		}
	}

	return null;
});

const mainActivityDelegatePrice = computed(() => {
	if (mainActivity.value) {
		return mainActivity.value.price;
	}

	return 'N/A';
});

const mainActivityGuestPrice = computed(() => {
	if (mainActivity.value) {
		return mainActivity.value.guest_price;
	}

	return 'N/A';
});

const mainActivityAllowsGuests = computed(() => {
	if (mainActivity.value) {
		return mainActivity.value.allows_guests != 'no';
	}

	return false;
});

/**
 * Join Waiting List
 */
const form = reactive({
	name: '',
	email: '',
	message: '',
	isProcessing: false
});

const handleModalClose = (dialog_id) => {
	if (dialog_id === window.location.hash.substring(1)) {
		window.history.pushState({}, '', window.location.pathname);
	}

	//Reset form
	errorMessage.value = '';
	waitlistSuccess.value = false;
	form.name = '';
	form.email = '';
	form.message = '';
	form.isProcessing = false;

	//And other form
	leavingErrorMessage.value = '';
	leavingProcessing.value = false;
	leavingSuccess.value = false;
}

const errorMessage = ref('');
const waitlistSuccess = ref(false);

const joinWaitingList = async () => {
	if (form.isProcessing) return;
	form.isProcessing = true;

    errorMessage.value = ''; // Clear previous errors

    try {
	    const response = await axios.post(`${window.portal.events_url}/${key.value}/waitlist`, {
			name: form.name,
			email: form.email,
			message: form.message
		});

		if (response.status == 200) {
			waitlistSuccess.value = true;

			eventsStore.setWaitlist(response.data.waitlist, key.value);
			eventsStore.setPermission('waitlist', false, key.value);
		}
    } catch (error) {
    	if (error.response) {
            errorMessage.value = error.response.data.message || 'Unable to join waiting list';
        } else {
            errorMessage.value = 'An error occurred. Please try again.';
        }
    } finally {
    	form.isProcessing = false;
    }
}

/**
 * Leave Waiting List (logged in users only)
 */
const leavingProcessing = ref(false);
const leavingErrorMessage =  ref('');
const leavingSuccess = ref(false);

const leaveWaitingList = async () => {
	if (leavingProcessing.value) return;
	leavingProcessing.value = true;

	leavingErrorMessage.value = '';

	try {
		const response = await axios.delete(`${window.portal.waitlists_url}/${event.value.waitlist.id}`);

		if (response.status == 200) {
			leavingSuccess.value = true;
			eventsStore.setWaitlist(null, key.value);
			eventsStore.setPermission('waitlist', response.data.can.waitlist, key.value);
		}
	} catch (error) {
		if (error.response) {
            leavingErrorMessage.value = error.response.data.message || 'Unable to leave waiting list';
        } else {
            leavingErrorMessage.value = 'An error occurred. Please try again.';
        }
	} finally {
		leavingProcessing.value = false;
	}
}
</script>

<template>
	<div class="wp-block-group is-layout-constrained alignfull" style="padding-left: var(--wp--preset--spacing--50); padding-right: var(--wp--preset--spacing--50); margin-top: var(--wp--preset--spacing--60); margin-bottom: var(--wp--preset--spacing--70);">
		<div class="wp-block-group is-layout-grid alignwide event-content-grid">
			<div class="wp-block-group is-layout-flow event-overview" v-html="event.description"></div>

			<div class="wp-block-group is-layout-flow event-sidebar" style="border-radius: 1rem; border: solid 1px var(--wp--preset--color--admin-grey-200); padding: var(--wp--preset--spacing--40);">
				<h2 class="has-large-font-size" v-html="labels.date_time"></h2>
				<p style="margin-top: var(--wp--preset--spacing--20);" v-html="event.date_range"></p>

				<template v-if="event.location">
					<h2 class="has-large-font-size" v-html="labels.location" style="margin-top: var(--wp--preset--spacing--40);"></h2>
					<p style="margin-top: var(--wp--preset--spacing--20);">
						<a class="event-link" v-if="event.location.url" :href="event.location.url" target="_blank" rel="noopener nofollow" v-html="event.location.name"></a>
						<span v-else v-html="event.location.name"></span>
						<template v-if="event.location.directions_url">
							<span>&nbsp;|&nbsp;<a class="event-link" :href="event.location.directions_url" target="_blank" rel="noopener nofollow" v-html="labels.directions"></a></span>
						</template>
					</p>
				</template>

				<template v-if="event.tabs.length > 1 || event.activity_count > 1">
					<h2 class="has-large-font-size" v-html="labels.registrations" style="margin-top: var(--wp--preset--spacing--40);"></h2>
					<p style="margin-top: var(--wp--preset--spacing--20);" v-html="event.bookings_open ? labels.registrations_open : labels.registrations_closed"></p>
				</template>

				<template v-else>
					<h2 class="has-large-font-size" v-html="labels.pricing" style="margin-top: var(--wp--preset--spacing--40);"></h2>
					<p style="margin-top: var(--wp--preset--spacing--20);">
						<span>{{delegateLabel}}:</span> <strong class="has-text-color has-fc-green-800-color" v-html="mainActivityDelegatePrice"></strong>

						<template v-if="mainActivityAllowsGuests">
							<br/>
							<span>{{labels.companion}}:</span> <strong class="has-text-color has-fc-green-800-color" v-html="mainActivityGuestPrice"></strong>
						</template>
					</p>
				</template>

				<p v-if="event.tabs.length <= 1 && event.activity_count <= 1 && !event.bookings_open" v-html="labels.registrations_closed"></p>

				<div class="wp-block-buttons" v-if="event.bookings_open && event.can.register" style="margin-top: var(--wp--preset--spacing--40);">
					<div class="wp-block-button">
						<a class="wp-block-button__link" :href="eventsStore.getRegistrationUrl(event.key)" v-html="labels.register_now"></a>
					</div>
				</div>

				<div class="wp-block-buttons" v-else-if="event.show_waitlist && event.can.waitlist" style="margin-top: var(--wp--preset--spacing--40);">
					<div class="wp-block-button">
						<a class="wp-block-button__link" href="#waitlist" v-html="labels.join_waiting_list"></a>
					</div>
				</div>

				<div class="wp-block-buttons" v-else-if="authStore.isLoggedIn && event.waitlist" style="margin-top: var(--wp--preset--spacing--40);">
					<div class="wp-block-button">
						<a class="wp-block-button__link" href="#leave-waitlist" v-html="labels.leave_waiting_list"></a>
					</div>
				</div>
			</div>

			<div class="wp-block-group event-tabs" v-if="event.tabs.length > 1 || event.activity_count > 1">
				<div class="wp-block-group is-layout-flow" style="margin-bottom: var(--wp--preset--spacing--30);">
					<div class="wp-block-buttons tab-buttons is-layout-flex" style="gap: var(--wp--preset--spacing--20);">
						<div class="wp-block-button" v-for="tab in event.tabs">
							<a class="wp-block-button__link has-background has-text-color" style="font-weight: 400; padding: var(--wp--preset--spacing--10) var(--wp--preset--spacing--20); font-size: var(--wp--preset--font-size--xsmall);" :class="tab.key == activeTab ? 'has-fc-grey-600-background-color has-white-color' : 'has-admin-grey-150-background-color has-fc-grey-600-color'" v-html="tab.label" @click.prevent="activeTab = tab.key"></a>
						</div>
					</div>
				</div>

				<div class="wp-block-group is-layout-flow" v-for="tab in event.tabs" v-show="tab.key == activeTab" style="border-radius: 1rem; border: solid 1px var(--wp--preset--color--admin-grey-200); padding: var(--wp--preset--spacing--40);">
					<div class="wp-block-group is-layout-flow" v-if="tab.content" v-html="tab.content"></div>
					<div class="wp-block-group is-layout-flow" v-else-if="tab.activities">
						<div class="wp-block-group is-layout-flow activity-group" v-for="(activity, index) in tab.activities" :key="`activity-${activity.id}`">
                            <h2 class="has-medium-font-size" v-html="activity.name"></h2>

                            <p class="has-xsmall-font-size" style="margin-top: var(--wp--preset--spacing--10);">
                                <strong v-html="labels.cost"></strong> {{delegateLabel}} ({{activity.price}})
                                <span v-if="activity.allows_guests != 'no'"> | {{labels.companion}} ({{activity.guest_price}})</span>
                            </p>

                            <template v-if="activity.location && (!event.location || activity.location.id != event.location.id)">
                                <p class="has-xsmall-font-size" style="margin-top: var(--wp--preset--spacing--10);">
                                    <strong>{{labels.location}}: </strong>

                                    <a class="event-link" v-if="activity.location.url" :href="activity.location.url" target="_blank" rel="noopener nofollow" v-html="activity.location.name"></a>
									<span v-else v-html="activity.location.name"></span>
                                </p>

                                <p class="has-xsmall-font-size" v-if="activity.location.address" style="margin-top: var(--wp--preset--spacing--10);">
                                    <strong>{{labels.address}}: </strong>
                                    <span v-html="activity.location.address"></span>
                                    <span v-if="activity.location.directions_url"> | <a class="event-link" :href="activity.location.directions_url" target="_blank" v-html="labels.directions"></a></span>
                                </p>
                            </template>

                            <p class="has-small-font-size" v-if="activity.description" v-html="activity.description" style="margin-top: var(--wp--preset--spacing--20);"></p>
                        </div>
					</div>
				</div>
			</div>
		</div>
	</div>

	<dialog v-if="(event && event.can.waitlist) || waitlistSuccess" id="waitlist">
		<form class="close" method="dialog" @submit="handleModalClose('waitlist')">
			<button value="close">
				<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M8.93359 1.49609L5.65234 4.77734L8.90625 8.03125C9.17969 8.27734 9.17969 8.6875 8.90625 8.93359C8.66016 9.20703 8.25 9.20703 8.00391 8.93359L4.72266 5.67969L1.46875 8.93359C1.22266 9.20703 0.8125 9.20703 0.566406 8.93359C0.292969 8.6875 0.292969 8.27734 0.566406 8.00391L3.82031 4.75L0.566406 1.49609C0.292969 1.25 0.292969 0.839844 0.566406 0.566406C0.8125 0.320312 1.22266 0.320312 1.49609 0.566406L4.75 3.84766L8.00391 0.59375C8.25 0.320312 8.66016 0.320312 8.93359 0.59375C9.17969 0.839844 9.17969 1.25 8.93359 1.49609Z" fill="#1E2120"/>
				</svg>
			</button>
		</form>

		<div class="wp-block-group is-vertical is-content-justification-center is-layout-flex" id="waitlist-form">
			<h3 class="has-h-4-font-size" v-html="labels.join_waiting_list"></h3>

			<p v-html="labels.event_organizations_will_reach_out"></p>

			<div class="wp-block-group is-layout-flow" v-if="errorMessage" style="border-radius: var(--wp--custom--border--radius-10); border: solid 1px var(--wp--preset--color--red-700); background-color: var(--wp--preset--color--red-100); padding: var(--wp--preset--spacing--10) var(--wp--preset--spacing--20);">
				<p class="has-small-font-size has-text-color has-red-700-color" v-html="errorMessage"></p>
			</div>

			<div class="wp-block-group is-layout-flow" v-if="waitlistSuccess" style="border-radius: var(--wp--custom--border--radius-10); border: solid 1px var(--wp--preset--color--fc-green-800); background-color: var(--wp--preset--color--fc-green-50); padding: var(--wp--preset--spacing--10) var(--wp--preset--spacing--20);">
				<p class="has-small-font-size has-text-color has-fc-green-800-color" v-html="labels.join_waiting_list_success"></p>
			</div>

			<form v-else class="wp-block-group is-vertical is-content-justification-center is-layout-flex waitlist-form" style="align-items: flex-start;" @submit.prevent="joinWaitingList">
				<div v-if="!authStore.isLoggedIn">
					<label class="screen-reader-text" for="waitlist_name" v-html="labels.full_name"></label>

					<input id="waitlist_name" type="text" required v-model="form.name" :placeholder="labels.full_name">
				</div>

				<div v-if="!authStore.isLoggedIn">
					<label class="screen-reader-text" for="waitlist_email" v-html="labels.email"></label>

					<input id="waitlist_email" type="email" required v-model="form.email" autocomplete="email" :placeholder="labels.email">
				</div>

				<div>
					<label class="screen-reader-text" for="waitlist_comments" v-html="labels.comments"></label>

					<textarea id="waitlist_comments" v-model="form.message" :placeholder="labels.enter_comments"></textarea>
				</div>

				<button type="submit" :disabled="form.isProcessing" class="submit-button">
					<span v-if="form.isProcessing" class="spinner"></span>
					<span v-else v-html="labels.join_waiting_list"></span>
				</button>
			</form>
		</div>
	</dialog>

	<dialog v-if="(event && authStore.isLoggedIn && event.waitlist) || leavingSuccess" id="leave-waitlist">
		<form class="close" method="dialog" @submit="handleModalClose('leave-waitlist')">
			<button value="close">
				<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M8.93359 1.49609L5.65234 4.77734L8.90625 8.03125C9.17969 8.27734 9.17969 8.6875 8.90625 8.93359C8.66016 9.20703 8.25 9.20703 8.00391 8.93359L4.72266 5.67969L1.46875 8.93359C1.22266 9.20703 0.8125 9.20703 0.566406 8.93359C0.292969 8.6875 0.292969 8.27734 0.566406 8.00391L3.82031 4.75L0.566406 1.49609C0.292969 1.25 0.292969 0.839844 0.566406 0.566406C0.8125 0.320312 1.22266 0.320312 1.49609 0.566406L4.75 3.84766L8.00391 0.59375C8.25 0.320312 8.66016 0.320312 8.93359 0.59375C9.17969 0.839844 9.17969 1.25 8.93359 1.49609Z" fill="#1E2120"/>
				</svg>
			</button>
		</form>

		<div class="wp-block-group is-vertical is-content-justification-center is-layout-flex" id="leave-waitlist-form">
			<h3 class="has-h-4-font-size" v-html="labels.leave_waiting_list"></h3>

			<p v-html="labels.leave_waiting_list_confirm"></p>

			<div class="wp-block-group is-layout-flow" v-if="leavingErrorMessage" style="border-radius: var(--wp--custom--border--radius-10); border: solid 1px var(--wp--preset--color--red-700); background-color: var(--wp--preset--color--red-100); padding: var(--wp--preset--spacing--10) var(--wp--preset--spacing--20);">
				<p class="has-small-font-size has-text-color has-red-700-color" v-html="leavingErrorMessage"></p>
			</div>

			<div class="wp-block-group is-layout-flow" v-if="leavingSuccess" style="border-radius: var(--wp--custom--border--radius-10); border: solid 1px var(--wp--preset--color--fc-green-800); background-color: var(--wp--preset--color--fc-green-50); padding: var(--wp--preset--spacing--10) var(--wp--preset--spacing--20);">
				<p class="has-small-font-size has-text-color has-fc-green-800-color" v-html="labels.leave_waiting_list_success"></p>
			</div>

			<form v-else class="wp-block-group is-vertical is-content-justification-center is-layout-flex waitlist-form" style="align-items: flex-start;" @submit.prevent="leaveWaitingList">
				<button type="submit" :disabled="form.isProcessing" class="submit-button">
					<span v-if="form.isProcessing" class="spinner"></span>
					<span v-else v-html="labels.leave_waiting_list"></span>
				</button>
			</form>
		</div>
	</dialog>
</template>
